import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import utils from "../../../universal/utils";
import adminFetch from "../../../universal/adminFetch";
import useIsMountedRef from "../../../hooks/useIsMountedRef";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  input: {
    minWidth: 420,
    flex: 1,
  },
  status: {
    marginTop: theme.spacing(1),
  },
  counts: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
  },
  countBox: {
    minWidth: 120,
    padding: theme.spacing(1.5),
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 4,
  },
  tableWrap: {
    marginTop: theme.spacing(2),
    overflowX: 'auto',
  },
  advanced: {
    marginTop: theme.spacing(2),
  }
}));

function shortToken(token) {
  if (!token) return '-';
  if (token.length <= 10) return token;
  return `${token.slice(0, 6)}...${token.slice(-4)}`;
}

function formatDate(ms) {
  return typeof ms === 'number' ? new Date(ms).toISOString() : '-';
}

export default function SteamTokenSettings({ userId }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pool, setPool] = useState({ counts: {}, bySource: [], tokens: [] });
  const [steamAccountToken, setSteamAccountToken] = useState('');
  const [updatedAtMs, setUpdatedAtMs] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [legacyOpen, setLegacyOpen] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const [poolRes, legacyRes] = await Promise.all([
        adminFetch(`${basePath}/api/v7/admin/steam-tokens/adpass/${userId}?status=active`),
        adminFetch(`${basePath}/api/v7/admin/settings/steam-token/adpass/${userId}`)
      ]);
      const poolData = await poolRes.json();
      const legacyData = await legacyRes.json();
      if (!poolData?.ok) throw new Error(poolData?.err || poolData?.error || 'Failed to load steam token pool');
      if (!legacyData?.ok) throw new Error(legacyData?.err || 'Failed to load legacy steam token');
      if (!isMountedRef.current) return;
      setPool({
        counts: poolData.counts || {},
        bySource: poolData.bySource || [],
        tokens: poolData.tokens || []
      });
      setSteamAccountToken(legacyData.steamAccountToken || '');
      setUpdatedAtMs(typeof legacyData.updatedAtMs === 'number' ? legacyData.updatedAtMs : null);
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [basePath, isMountedRef, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    if (!userId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/steam-token/adpass/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ steamAccountToken })
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to save steam token');
      if (!isMountedRef.current) return;
      setSteamAccountToken(data.steamAccountToken || steamAccountToken);
      setUpdatedAtMs(typeof data.updatedAtMs === 'number' ? data.updatedAtMs : Date.now());
      setSuccess('Saved. Agents will receive the new token on their next announce (and before warmup).');
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [basePath, isMountedRef, userId, steamAccountToken]);

  const postTokenAction = useCallback(async (path, body, successMessage) => {
    if (!userId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || data?.error || 'Action failed');
      if (!isMountedRef.current) return;
      setSuccess(successMessage);
      await load();
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [basePath, isMountedRef, load, userId]);

  const releaseLease = useCallback((token) => {
    postTokenAction(
      `/api/v7/admin/steam-tokens/release-lease/adpass/${userId}`,
      { token },
      'Released token lease.'
    );
  }, [postTokenAction, userId]);

  const disableToken = useCallback((token) => {
    postTokenAction(
      `/api/v7/admin/steam-tokens/disable/adpass/${userId}`,
      { token },
      'Disabled token.'
    );
  }, [postTokenAction, userId]);

  return (
    <Paper className={classes.paper}>
      <Box className={classes.headerRow}>
        <Box>
          <Typography variant="h6">Steam Token (STEAM_ACCOUNT)</Typography>
          <Typography variant="body2" color="textSecondary">
            Edge leases one GSLT per running agent so parallel CS2 servers do not share a Steam identity.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpen((v) => !v)}
          startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box className={classes.counts}>
          {['total', 'active', 'available', 'leased'].map((key) => (
            <Box key={key} className={classes.countBox}>
              <Typography variant="caption" color="textSecondary">{key}</Typography>
              <Typography variant="h6">{pool.counts?.[key] || 0}</Typography>
            </Box>
          ))}
        </Box>

        <Box className={classes.row}>
          <Button
            variant="outlined"
            onClick={load}
            disabled={loading || saving || !userId}
          >
            Refresh
          </Button>
        </Box>

        <Box className={classes.tableWrap}>
          <Typography variant="subtitle1">Token pool</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Token</TableCell>
                <TableCell>Memo</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Lease</TableCell>
                <TableCell>Last sync</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(pool.tokens || []).map((token) => (
                <TableRow key={token.token}>
                  <TableCell>{shortToken(token.token)}</TableCell>
                  <TableCell>{token.memo || '-'}</TableCell>
                  <TableCell>{token.steamAccountSource || 'unknown'}</TableCell>
                  <TableCell>{token.status}</TableCell>
                  <TableCell>{token.leased ? token.leasedToInstanceId || 'leased' : 'available'}</TableCell>
                  <TableCell>{formatDate(token.lastSyncedFromSteamAtMs)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() => releaseLease(token.token)}
                      disabled={loading || saving || !token.leased}
                    >
                      Release
                    </Button>
                    <Button
                      size="small"
                      onClick={() => disableToken(token.token)}
                      disabled={loading || saving || token.status === 'disabled'}
                    >
                      Disable
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {(!pool.tokens || pool.tokens.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="textSecondary">
                      No tokens synced yet. Use the Chrome extension on Steam Game Server Account Management.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Box>

        <Box className={classes.advanced}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setLegacyOpen((v) => !v)}
          >
            {legacyOpen ? 'Hide legacy token' : 'Advanced: legacy single token'}
          </Button>
          <Collapse in={legacyOpen} timeout="auto" unmountOnExit>
            <Box className={classes.row}>
              <TextField
                className={classes.input}
                label="Legacy STEAM_ACCOUNT"
                variant="outlined"
                size="small"
                type="password"
                value={steamAccountToken}
                onChange={(e) => setSteamAccountToken(e.target.value)}
                disabled={loading || saving || !userId}
                helperText={updatedAtMs ? `Last updated: ${new Date(updatedAtMs).toISOString()}` : 'Using default until changed'}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={save}
                disabled={loading || saving || !userId}
              >
                {saving ? <CircularProgress size={18} /> : 'Save legacy'}
              </Button>
            </Box>
          </Collapse>
        </Box>

        {loading ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Loading…
          </Typography>
        ) : null}

        {error ? (
          <Typography className={classes.status} variant="body2" color="error">
            {error}
          </Typography>
        ) : null}

        {success ? (
          <Typography className={classes.status} variant="body2" style={{ color: '#1b5e20' }}>
            {success}
          </Typography>
        ) : null}
      </Collapse>
    </Paper>
  );
}

